import { useState, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import { getStructureGridColumns, DEFAULT_COLUMN_VISIBILITY } from '../config/gridColumns';

/**
 * Hook for managing data grid column configuration
 * Handles column definitions and visibility state
 */
export function useColumnConfiguration({ isPreviewMode = false, previewData = [] } = {}) {
  const theme = useTheme();
  
  // Column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(DEFAULT_COLUMN_VISIBILITY);
  
  // Column definitions with theme-based widths
  const columns = useMemo(() => {
    const baseColumns = getStructureGridColumns({ isPreviewMode, previewData, theme });
    return baseColumns.map(col => ({
      ...col,
      resizable: true  // Enable resizing for all columns
    }));
  }, [isPreviewMode, previewData, theme]);
  
  // Toggle visibility of a specific column
  const toggleColumnVisibility = useCallback((field) => {
    setColumnVisibilityModel(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  }, []);
  
  // Show all columns
  const showAllColumns = useCallback(() => {
    const allVisible = {};
    columns.forEach(col => {
      allVisible[col.field] = true;
    });
    setColumnVisibilityModel(allVisible);
  }, [columns]);
  
  // Reset to default visibility
  const resetColumnVisibility = useCallback(() => {
    setColumnVisibilityModel(DEFAULT_COLUMN_VISIBILITY);
  }, []);
  
  // Get visible column count
  const visibleColumnCount = useMemo(() => {
    return Object.values(columnVisibilityModel).filter(visible => visible).length;
  }, [columnVisibilityModel]);
  
  // Check if a column is visible
  const isColumnVisible = useCallback((field) => {
    return columnVisibilityModel[field] !== false;
  }, [columnVisibilityModel]);
  
  return {
    // Column definitions
    columns,
    
    // Visibility state
    columnVisibilityModel,
    setColumnVisibilityModel,
    
    // Methods
    toggleColumnVisibility,
    showAllColumns,
    resetColumnVisibility,
    isColumnVisible,
    
    // Computed values
    visibleColumnCount
  };
}